import type {
    PersistedUserEventRecord,
    PersistedUserEventSmsConsentMetadata,
} from '@fruitful/types';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';

import type { NotificationType } from '../settings/types/UserNotificationChannel';
import { NotificationChannel } from '../settings/types/UserNotificationChannel';
import type { User } from '../user/types/User';

export const isNotificationEnabled = async (
    user: User,
    type: NotificationType,
    channel: NotificationChannel,
    smsConsentUserEvents: PersistedUserEventRecord[] | undefined,
): Promise<boolean> => {
    const notificationChannel = (user.userNotificationChannels ?? []).find(
        (n) => n.channel === channel && n.type === type,
    );

    // If it is a push notification and the user has not granted access to push notifications on their device, override to false.
    if (channel === NotificationChannel.PUSH) {
        const hasGrantedPushNotifications =
            await hasGrantedPushNotificationsPermission();
        if (!hasGrantedPushNotifications) {
            return false;
        }
    }

    // If it is an SMS notification and the user has not opted in to SMS notifications writ large, override to false
    if (channel === NotificationChannel.SMS) {
        const hasSmsConsent = userHasSmsConsentForCurrentPhoneNumber(
            smsConsentUserEvents,
            user,
        );
        if (!hasSmsConsent) {
            return false;
        }
    }

    // Default to true if notification channel preference is not explicitly defined.
    return notificationChannel == null || notificationChannel.enabled;
};

export const getPushNotificationsPermissions = async () => {
    return Device.isDevice ? await Notifications.getPermissionsAsync() : null;
};

export const hasGrantedPushNotificationsPermission = async () => {
    if (Device.isDevice) {
        const settings = await getPushNotificationsPermissions();
        if (settings == null) {
            return false;
        }

        return (
            settings.granted ||
            settings.ios?.status ===
                Notifications.IosAuthorizationStatus.PROVISIONAL
        );
    } else {
        return false;
    }
};

const getMetadataPhoneNumber = (
    metadata: PersistedUserEventSmsConsentMetadata | null | undefined,
): string | undefined => {
    if (metadata == null || typeof metadata !== 'object') {
        return undefined;
    }
    if (!('phoneNumber' in metadata)) {
        return undefined;
    }

    return typeof metadata.phoneNumber === 'string'
        ? metadata.phoneNumber
        : undefined;
};

export const userHasSmsConsentForCurrentPhoneNumber = (
    events: PersistedUserEventRecord[] | undefined,
    user: User,
): boolean => {
    if (
        user.phoneNumber == null ||
        user.phoneNumber === '' ||
        events == null ||
        events.length === 0
    ) {
        return false;
    }

    return events.some(
        (event) =>
            getMetadataPhoneNumber(
                event.metadata as
                    | PersistedUserEventSmsConsentMetadata
                    | null
                    | undefined,
            ) === user.phoneNumber,
    );
};
